import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppTopbar from "../components/AppTopbar";
import { ROUTES } from "../config/appConfig";
import { companyInfo, vehicleTypes } from "../config/companyConfig";
import { setDropLocation, setPickupLocation, setSelectedVehicle } from "../store/slices/deliverySlice";
import { calculateDistance, calculateFare, getTimeEstimate } from "../utils/fareCalculator";
import "../styles/Landing.css";

const LandingEstimateStep = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [routeDraft, setRouteDraft] = useState(null);
  const [selectedVehicle, setSelectedVehicleState] = useState(null);
  const [showFareBreakdown, setShowFareBreakdown] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const storedRoute = window.sessionStorage.getItem("landingRouteDraft");
    const storedVehicle = window.sessionStorage.getItem("landingSelectedVehicle");

    if (!storedRoute) {
      navigate(ROUTES.home, { replace: true });
      return;
    }

    if (!storedVehicle) {
      navigate(ROUTES.bookVehicle, { replace: true });
      return;
    }

    try {
      setRouteDraft(JSON.parse(storedRoute));
      setSelectedVehicleState(storedVehicle);
    } catch (error) {
      navigate(ROUTES.home, { replace: true });
    }
  }, [navigate]);

  const distance = useMemo(() => {
    if (!routeDraft?.pickupLocation || !routeDraft?.dropLocation) {
      return null;
    }

    return calculateDistance(
      routeDraft.pickupLocation.lat,
      routeDraft.pickupLocation.lng,
      routeDraft.dropLocation.lat,
      routeDraft.dropLocation.lng
    );
  }, [routeDraft]);

  const fareEstimate = useMemo(() => {
    if (!distance || !selectedVehicle) {
      return null;
    }

    return calculateFare(distance, selectedVehicle);
  }, [distance, selectedVehicle]);

  const timeEstimate = useMemo(() => {
    if (!distance || !selectedVehicle) {
      return null;
    }

    return getTimeEstimate(distance, selectedVehicle);
  }, [distance, selectedVehicle]);

  const handleProceedToLogin = () => {
    if (!routeDraft || !selectedVehicle) {
      return;
    }

    dispatch(setPickupLocation(routeDraft.pickupLocation));
    dispatch(setDropLocation(routeDraft.dropLocation));
    dispatch(setSelectedVehicle(selectedVehicle));
    window.sessionStorage.setItem("landingBookingFlow", "true");
    navigate(ROUTES.login, {
      state: {
        postLoginFocus: "create-delivery",
        fromLandingFlow: true,
      },
    });
  };

  const handleCustomerLogin = () => {
    [
      "landingBookingFlow",
      "landingRouteDraft",
      "landingSelectedVehicle",
    ].forEach((key) => window.sessionStorage.removeItem(key));
    navigate(ROUTES.login);
  };

  if (!routeDraft || !selectedVehicle) {
    return null;
  }

  return (
    <div className="landing-page">
      <AppTopbar
        brandHref={ROUTES.home}
        title={companyInfo.name}
        navId="landing-step-three-nav"
        pillText="Fare Estimate"
        actions={(
          <Button variant="outline-light" onClick={handleCustomerLogin}>
            For Customer
          </Button>
        )}
      />

      <section className="estimator-section">
        <Container>
          <div className="section-intro text-center">
            <span className="section-kicker">Fare Estimate</span>
            <h2 className="section-title">Review the fare estimate here</h2>
          </div>

          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="estimator-form">
                <div className="route-section-shell">
                  <div className="route-section-heading">
                    <span className="route-section-title">Saved booking inputs</span>
                    <small>Everything is ready. Review the estimate and continue.</small>
                  </div>

                  <div className="summary-grid">
                    <div className="summary-chip">
                      <span>Pickup</span>
                      <strong>{routeDraft.pickupLocation?.name}</strong>
                    </div>
                    <div className="summary-chip">
                      <span>Drop</span>
                      <strong>{routeDraft.dropLocation?.name}</strong>
                    </div>
                    <div className="summary-chip">
                      <span>Vehicle</span>
                      <strong>{vehicleTypes[selectedVehicle]?.name}</strong>
                    </div>
                    <div className="summary-chip">
                      <span>ETA</span>
                      <strong>{timeEstimate}</strong>
                    </div>
                  </div>

                  <div className="route-progress-card">
                    <span className="route-progress-pill is-complete">Route</span>
                    <span className="route-progress-pill is-complete">Vehicle</span>
                    <span className="route-progress-pill is-complete">Estimate</span>
                    <span className="route-progress-pill">Next: Login</span>
                  </div>
                </div>

                {fareEstimate?.error && <Alert variant="warning">{fareEstimate.error}</Alert>}

                <h4 className="fare-title">Fare Estimate</h4>
                <p className="fare-subtitle">
                  Review the estimate now, then continue.
                </p>

                {fareEstimate && !fareEstimate.error && (
                  <>
                    <div className="fare-display">
                      <div className="fare-amount">
                        <span className="currency">Rs</span>
                        <span className="amount">{fareEstimate.totalFare}</span>
                      </div>
                      <p className="vehicle-selected">{vehicleTypes[selectedVehicle]?.name}</p>
                    </div>

                    <div className="summary-grid">
                      <div className="summary-chip">
                        <span>Route</span>
                        <strong>{distance} km</strong>
                      </div>
                      <div className="summary-chip">
                        <span>ETA</span>
                        <strong>{timeEstimate}</strong>
                      </div>
                    </div>

                    <div
                      className="fare-breakdown-toggle"
                      onClick={() => setShowFareBreakdown(!showFareBreakdown)}
                    >
                      <span>Breakdown</span>
                      <span>{showFareBreakdown ? "Hide" : "Show"}</span>
                    </div>

                    {showFareBreakdown && (
                      <div className="fare-breakdown">
                        <div className="breakdown-row">
                          <span>Base Fare</span>
                          <span>Rs {fareEstimate.baseFare}</span>
                        </div>
                        <div className="breakdown-row">
                          <span>Distance Charge ({distance} km)</span>
                          <span>Rs {fareEstimate.distanceCharge}</span>
                        </div>
                        <div className="breakdown-row">
                          <span>Platform Fee</span>
                          <span>Rs {fareEstimate.platformFee}</span>
                        </div>
                        {fareEstimate.surge > 1 && (
                          <div className="breakdown-row">
                            <span>Surge Charge ({fareEstimate.surge}x)</span>
                            <span className="surge-text">Applied</span>
                          </div>
                        )}
                        <div className="breakdown-row">
                          <span>GST (5%)</span>
                          <span>Rs {fareEstimate.gst}</span>
                        </div>
                        <div className="breakdown-row total">
                          <span>Total</span>
                          <span>Rs {fareEstimate.totalFare}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div className="step-action-row">
                  <Button variant="outline-secondary" className="route-action-btn step-previous-btn" onClick={() => navigate(ROUTES.bookVehicle)}>
                    Previous
                  </Button>
                  <Button
                    className="btn-book-now step-proceed-btn"
                    size="lg"
                    onClick={handleProceedToLogin}
                    disabled={!fareEstimate || !!fareEstimate.error}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default LandingEstimateStep;
